import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const serviceLinks = [
  {
    name: 'Web Development',
    href: '/services/web-development',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    )
  },
  {
    name: 'Data Analytics',
    href: '/services/data-analytics',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )
  },
  {
    name: 'Custom Software',
    href: '/services/custom-software-development',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    )
  },
  {
    name: 'Mobile App Development',
    href: '/services/mobile-app-development',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    )
  },
  {
    name: 'Cloud Solutions',
    href: '/services/cloud-solutions',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    )
  },
  {
    name: 'UI/UX Design',
    href: '/services/ui-ux-design',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
      </svg>
    )
  },
  {
    name: 'DevOps & AWS Services',
    href: '/services/devops-aws-services',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    )
  }
];

const mainLinks = [
  { name: 'Home', href: '/' },
  { name: 'About Us', href: '/about-us' },
  { name: 'Products', href: '/products' },
  { name: 'Services', href: '/services', hasDropdown: true },
  { name: 'Blogs', href: '/blogs' },
  { name: 'Contact Us', href: '/contact-us' }
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const servicesLeaveTimer = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileServicesOpen(false);
    setIsServicesOpen(false);
  }, [location.pathname]);

  const handleServicesEnter = () => {
    clearTimeout(servicesLeaveTimer.current);
    setIsServicesOpen(true);
  };

  const handleServicesLeave = () => {
    servicesLeaveTimer.current = setTimeout(() => setIsServicesOpen(false), 150);
  };

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 w-[95%] max-w-5xl border border-white/20
        ${isMobileMenuOpen ? 'rounded-2xl' : 'rounded-full'}
        ${isScrolled ? 'bg-[#3b5998]/95 backdrop-blur-md shadow-xl shadow-blue-950/30' : 'bg-[#3b5998]/90 backdrop-blur-md shadow-lg shadow-blue-950/20'}`}
    >
      <nav className="mx-auto px-6">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/">
              <img
                src="/images/Scalizap-edited.png"
                alt="Scalizap Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {mainLinks.map((link) =>
              link.hasDropdown ? (
                /* Services with dropdown */
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={handleServicesEnter}
                  onMouseLeave={handleServicesLeave}
                >
                  <Link
                    to={link.href}
                    className={`flex items-center gap-1 px-3.5 py-2 font-medium text-sm transition-colors duration-200 rounded-lg ${isActive(link.href)
                        ? 'text-white bg-white/20 shadow-inner font-semibold'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                      }`}
                  >
                    {link.name}
                    <svg
                      className={`w-3 h-3 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Link>

                  {/* Dropdown — wrapped in transparent padding so mouse doesn't leave hover zone */}
                  <div
                    className={`absolute top-full left-1/2 -translate-x-1/2 pt-2 transition-all duration-200 ${isServicesOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-1'
                      }`}
                    style={{ minWidth: '260px' }}
                    onMouseEnter={handleServicesEnter}
                    onMouseLeave={handleServicesLeave}
                  >
                    <div className="bg-[#2e477a] rounded-2xl shadow-2xl border border-white/15 py-2 overflow-hidden">
                      <div className="px-4 py-2 border-b border-white/10 mb-1">
                        <p className="text-xs font-semibold text-white/60 uppercase tracking-wider">Our Services</p>
                      </div>
                      {serviceLinks.map((svc) => (
                        <Link
                          key={svc.name}
                          to={svc.href}
                          className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-150 group ${location.pathname === svc.href
                              ? 'text-white bg-white/20 font-semibold'
                              : 'text-white/80 hover:text-white hover:bg-white/10'
                            }`}
                        >
                          <span className={`flex-shrink-0 ${location.pathname === svc.href ? 'text-[#f9831d]' : 'text-cyan-300 group-hover:text-white'}`}>
                            {svc.icon}
                          </span>
                          {svc.name}
                        </Link>
                      ))}
                      <div className="px-4 pt-2 mt-1 border-t border-white/10">
                        <Link
                          to="/services"
                          className="flex items-center justify-center gap-1 w-full py-2 text-xs font-semibold text-[#f9831d] hover:text-orange-300 transition-colors"
                        >
                          View All Services
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={link.href === '/' ? () => window.scrollTo({ top: 0, behavior: 'smooth' }) : undefined}
                  className={`px-3.5 py-2 font-medium text-sm transition-colors duration-200 rounded-lg ${isActive(link.href)
                      ? 'text-white bg-white/20 shadow-inner font-semibold'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                >
                  {link.name}
                </Link>
              )
            )}

            <Link
              to="/contact-us"
              className="ml-4 bg-[#f9831d] hover:bg-[#e47210] text-white px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-orange-500/25 focus:outline-none focus:ring-4 focus:ring-orange-300/40"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile hamburger */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-full text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200"
              aria-label="Toggle menu"
            >
              {!isMobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${isMobileMenuOpen ? 'max-h-[600px] opacity-100 pb-4' : 'max-h-0 opacity-0'
            }`}
        >
          <div className="pt-2 border-t border-white/15 mt-2 space-y-1">
            {mainLinks.map((link) =>
              link.hasDropdown ? (
                <div key={link.name}>
                  <button
                    onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                    className="flex items-center justify-between w-full text-white/90 hover:text-white px-3 py-2.5 text-sm font-medium transition-all rounded-xl hover:bg-white/10"
                  >
                    {link.name}
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${isMobileServicesOpen ? 'max-h-96' : 'max-h-0'}`}>
                    <div className="pl-3 pt-1 space-y-0.5">
                      {serviceLinks.map((svc) => (
                        <Link
                          key={svc.name}
                          to={svc.href}
                          className="flex items-center gap-2 text-white/80 hover:text-white px-3 py-2 text-sm rounded-xl hover:bg-white/10 transition-all"
                        >
                          <span className="text-cyan-300">{svc.icon}</span>
                          {svc.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={link.href === '/' ? () => window.scrollTo({ top: 0, behavior: 'smooth' }) : undefined}
                  className={`block px-3 py-2.5 text-sm font-medium rounded-xl transition-all ${isActive(link.href) ? 'text-white bg-white/20 font-semibold' : 'text-white/90 hover:text-white hover:bg-white/10'
                    }`}
                >
                  {link.name}
                </Link>
              )
            )}
            <div className="pt-2">
              <Link
                to="/contact-us"
                className="block bg-[#f9831d] text-white px-3 py-3 text-sm font-semibold rounded-xl text-center transition-all hover:bg-[#e47210]"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
